/* eslint-disable @typescript-eslint/no-explicit-any */
import { ListPerson } from "../../../../List/schema/list";
import { FewShotExample, MAX_FEW_SHOT_EXAMPLES } from "../schema/aiSnippet";

/**
 * Strips simple HTML tags so the few-shot example text reads as plain prose.
 * The full message body comes out of TinyMCE as HTML; the LLM wants prose.
 */
const stripBasicHtml = (html: string): string => {
	if (typeof window === "undefined") {
		return html;
	}

	const doc = window.document.createElement("div");

	doc.innerHTML = html;
	const text = doc.textContent || doc.innerText || "";

	return text
		.replace(/\s+\n/g, "\n")
		.replace(/\n{3,}/g, "\n\n")
		.trim();
};

/**
 * Resolves `{key}` and `{key|label}` placeholders inside a message body using
 * the given lead's data. Unknown keys are left as-is; `column_*` (AI snippet)
 * tokens are left untouched so the user can see they would be replaced at
 * runtime by AI output.
 */
export const resolveVariablesForLead = (body: string, lead: ListPerson | undefined): string => {
	if (!body || !lead) {
		return body;
	}

	const ctx: any = { prospect: lead.prospect, list: lead.list, person: lead.prospect };

	const lookup = (key: string): string | undefined => {
		if (key.startsWith("column_")) {
			return undefined;
		}

		const parts = key.split("_");
		let cursor: any = ctx;

		for (const p of parts) {
			if (cursor == null) {
				return undefined;
			}

			cursor = Array.isArray(cursor[p]) ? cursor[p][0] : cursor[p];
		}

		if (cursor == null || typeof cursor === "object") {
			return undefined;
		}

		return String(cursor);
	};

	return body.replace(/\{([^{}|]+)(?:\|[^{}]*)?\}/g, (match, rawKey) => {
		const resolved = lookup(rawKey.trim());

		return resolved ?? match;
	});
};

/**
 * Pre-builds few-shot example rows from the snippet's prompt instructions.
 * For each available lead, the "Example instructions" field is populated with
 * the snippet prompt with variables resolved using that lead's data. The
 * "Ideal output" remains blank for the user to fill in with the snippet text
 * they want the AI to produce for that lead.
 */
export const buildFewShotFromLeads = (
	leads: ListPerson[],
	promptInstructions: string
): FewShotExample[] => {
	if (!leads || leads.length === 0 || !promptInstructions) {
		return [];
	}

	const plain = stripBasicHtml(promptInstructions);
	const used = new Set<number>();
	const examples: FewShotExample[] = [];

	for (const lead of leads) {
		if (examples.length >= MAX_FEW_SHOT_EXAMPLES) {
			break;
		}

		if (lead.id != null && used.has(lead.id)) {
			continue;
		}

		const resolvedMessage = resolveVariablesForLead(plain, lead);

		if (!resolvedMessage) {
			continue;
		}

		examples.push({ leadContext: resolvedMessage, idealOutput: "" });

		if (lead.id != null) {
			used.add(lead.id);
		}
	}

	return examples;
};
